/**
 * The Forsthofgut Akademie's fields on an employee profile, and where the section gets its options.
 *
 * All four values live in the profile's `tags` map, not in its own fields. Many of Forsthofgut's
 * employee profiles come from the PMS, and a PMS sync rewrites the profile type and department on
 * every push, so anything set there in the dashboard would be undone. The sync never touches tags.
 *
 * The keys mirror `AcademyTags.kt` on the API and `academy.util.ts` in the app, and values are
 * strings like everything else in `tags`: a flag is "true", a list is comma separated.
 */
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import type {DataTags} from "../../common/dataTags.util";
import {academyTagList, withAcademyTag} from "../../common/academyTags.util";

export const AcademyProfileTags = {
    EMPLOYEE: 'academy_employee',
    DEPARTMENT: 'academy_department',
    LEVEL: 'academy_level',
    APPROVES_FOR: 'academy_approves_for',
} as const

/** Owned by the Academy section, so the raw tag editor below it must not offer them twice. */
export const ACADEMY_PROFILE_TAG_KEYS: readonly string[] = Object.values(AcademyProfileTags)

/** On the Akademie sector: the hotel's departments, comma separated, as the section offers them. */
export const ACADEMY_DEPARTMENTS_TAG = 'academy_departments'

/**
 * Switching the team member flag off removes only the flag.
 *
 * Level, department and approval rights stay where they were, so switching somebody back on later
 * does not mean entering them again. None of them grant anything on their own: the API reads them
 * only for a profile that carries the flag.
 */
export const withAcademyFlag = (tags: DataTags | null | undefined, key: string, on: boolean): DataTags =>
    withAcademyTag(tags, key, on ? 'true' : undefined)

/**
 * The departments to offer: the Akademie sector's list, plus anything this profile already holds.
 *
 * The profile's own values are kept even when the list no longer has them, so a renamed or
 * removed department still shows on the people who carry it instead of silently disappearing from
 * the form, and can be corrected rather than lost.
 */
export const academyDepartmentOptions = (sector: Sector | undefined, tags: DataTags | null | undefined): string[] => {
    const configured = academyTagList(sector?.tags as DataTags | undefined, ACADEMY_DEPARTMENTS_TAG)
    const held = [
        ...academyTagList(tags, AcademyProfileTags.DEPARTMENT),
        ...academyTagList(tags, AcademyProfileTags.APPROVES_FOR),
    ]
    return Array.from(new Set([...configured, ...held]))
}
